//VERY IMPORTANT DO NOT REMOVE OR DELETE THIS FILE. IT IS USED FOR SPEECH TO TEXT CONVERSION
// Migrated from Azure Speech SDK → Deepgram Nova-3 via native WebSocket + MediaRecorder

import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
} from "react";
import { techTerms } from "../utils/techTerms";

// -------------------- Context -------------------- //
const transcriptionContext = createContext();
export const useTranscriptionStatus = () => useContext(transcriptionContext);

// -------------------- Supported MIME type detection -------------------- //
const getSupportedMimeType = () => {
  const types = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
  ];
  for (const type of types) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return ""; // Let browser choose default
};

// -------------------- Provider -------------------- //
export const SpeechRecognizationProvider = ({ children }) => {
  const socketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const micStreamRef = useRef(null); // Dedicated ref so cleanup always finds the stream
  const answersRef = useRef({});
  const bufferRef = useRef({});
  const activeQuestionRef = useRef(0);
  const isStartingRef = useRef(false);
  const recognitionRef = useRef(null);

  // Monotonically increasing session counter — old async callbacks bail out if stale
  const sessionCountRef = useRef(0);

  const [isRecording, setIsRecording] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [displayTranscript, setDisplayTranscript] = useState("");
  const [isSpeaking, setIsSpeaking] = useState(false);

  const updateDisplayTranscript = useCallback((qIndex) => {
    const targetIndex = qIndex !== undefined ? qIndex : activeQuestionRef.current;
    const committed = answersRef.current[targetIndex] || "";
    const interim = bufferRef.current[targetIndex] || "";
    let combined = committed;
    if (interim) {
      combined = committed ? `${committed} ${interim}` : interim;
    }
    setDisplayTranscript(combined.trim());
  }, []);

  const audioAnalyzerRef = useRef(null);   // { audioContext, analyser, stream }
  const animationFrameIdRef = useRef(null);

  // -------------------- Normalize & Deduplicate Text -------------------- //
  const normalizeText = (text) =>
    (text || "").toLowerCase().replace(/[.,!?]/g, "").trim();

  const dedupeText = (text) => {
    if (!text) return "";
    let cleaned = text.trim();

    // Deduplicate identical or near-identical sentences
    const sentences = cleaned.split(/(?<=[.!?])\s+/);
    if (sentences.length > 1) {
      const uniqueSentences = [];
      for (const sentence of sentences) {
        const normS = normalizeText(sentence);
        if (!normS) continue;
        const isDuplicate = uniqueSentences.some((existing) => {
          const normE = normalizeText(existing);
          if (normE === normS) return true;
          // Check substring overlap of >70% if sentence is sufficiently long
          if (normE.length > 20 && normS.length > 20) {
            if (normE.includes(normS) || normS.includes(normE)) return true;
          }
          return false;
        });
        if (!isDuplicate) {
          uniqueSentences.push(sentence.trim());
        }
      }
      cleaned = uniqueSentences.join(" ");
    }

    // Deduplicate repeated multi-word phrases (e.g. repeated paragraphs without punctuation)
    const words = cleaned.split(/\s+/);
    if (words.length >= 10) {
      const half = Math.floor(words.length / 2);
      const firstHalf = words.slice(0, half).join(" ").toLowerCase();
      const secondHalf = words.slice(half).join(" ").toLowerCase();
      if (firstHalf === secondHalf || firstHalf.includes(secondHalf) || secondHalf.includes(firstHalf)) {
        cleaned = words.slice(0, half).join(" ");
      }
    }

    return cleaned;
  };

  // -------------------- Tech Term Corrections -------------------- //
  const corrections = {
    pythan: "Python", pithon: "Python", "pie thon": "Python",
    lstn: "LSTM", "el es tee em": "LSTM",
    tenserflow: "TensorFlow", "tensor flow": "TensorFlow",
    scikit: "Scikit-Learn", "sci kit": "Scikit-Learn",
    pandas: "Pandas", numpy: "NumPy", "num pie": "NumPy",
    pytorch: "PyTorch", "pie torch": "PyTorch",
    "hugging face": "Hugging Face", huggingface: "Hugging Face",
    "open c v": "OpenCV", "open sea v": "OpenCV",
    nlp: "NLP", "en el pee": "NLP",
    "c n n": "CNN", "r n n": "RNN",
    "gradient descent": "Gradient Descent",
    "back propagation": "Backpropagation",
    epochs: "Epochs", "e pocks": "Epochs", yolo: "YOLO",
    "gen ai": "GenAI", jenai: "GenAI",
    llm: "LLM", "el el em": "LLM", rag: "RAG",
    "agentic a i": "Agentic AI", "agentic ai": "Agentic AI", "authentic ai": "Agentic AI",
    chatgpt: "ChatGPT", "chat g p t": "ChatGPT",
    midjourney: "Midjourney", "mid journey": "Midjourney",
    langchain: "LangChain", "lang chain": "LangChain",
    "lama two": "Llama 2", "lama three": "Llama 3", lama: "Llama",
    gemini: "Gemini", "jem in eye": "Gemini", "open ai": "OpenAI",
    "c plus plus": "C++", "c sharp": "C#", "see sharp": "C#", "see plus plus": "C++",
    "goal ang": "Go", "go lang": "Go", java: "Java",
    "kot lin": "Kotlin", swift: "Swift", ruby: "Ruby", rust: "Rust",
    php: "PHP", "p h p": "PHP",
    "node dot js": "Node.js", "note js": "Node.js", "node js": "Node.js",
    "next dot js": "Next.js", "next js": "Next.js",
    "react js": "React", react: "React", "re act": "React",
    "vue js": "Vue.js", "view js": "Vue.js", angular: "Angular",
    "type script": "TypeScript", "java script": "JavaScript",
    django: "Django", flask: "Flask", "fast api": "FastAPI",
    "spring boot": "Spring Boot", html: "HTML", css: "CSS",
    tailwind: "Tailwind CSS", bootstrap: "Bootstrap",
    json: "JSON", "j son": "JSON", "jay son": "JSON",
    xml: "XML", graphql: "GraphQL", "graph q l": "GraphQL", "g r p c": "gRPC",
    "mango db": "MongoDB", "mongo db": "MongoDB",
    sequel: "SQL", "s q l": "SQL", "no sequel": "NoSQL",
    postgres: "PostgreSQL", "post gres": "PostgreSQL", "my sequel": "MySQL",
    oracle: "Oracle", redis: "Redis", cassandra: "Cassandra", "dynamo db": "DynamoDB",
    "neo for j": "Neo4j", hadoop: "Hadoop", spark: "Spark", "apache spark": "Apache Spark",
    kafka: "Kafka", "air flow": "Airflow", "data bricks": "Databricks", "snow flake": "Snowflake",
    api: "API", "a p i": "API", "rest a p i": "REST API",
    restful: "RESTful", "o auth": "OAuth", jwt: "JWT", "j w t": "JWT",
    "doc er": "Docker", "coober netties": "Kubernetes",
    "k eights": "Kubernetes", "k 8 s": "Kubernetes", "c i c d": "CI/CD",
    "git hub": "GitHub", "git lab": "GitLab",
    aws: "AWS", "a w s": "AWS", gcp: "GCP", "g c p": "GCP",
    azure: "Azure", "e c 2": "EC2", "s 3": "S3", lambda: "Lambda",
    "ver cell": "Vercel", netlify: "Netlify", heroku: "Heroku",
    agile: "Agile", scrum: "Scrum", jira: "Jira",
    ezsync: "EZSync", "easy sync": "EZSync",
  };

  const correctText = (text) => {
    let corrected = text;
    Object.entries(corrections).forEach(([wrong, right]) => {
      const regex = new RegExp(`\\b${wrong}\\b`, "gi");
      corrected = corrected.replace(regex, right);
    });
    return corrected;
  };

  // -------------------- Waveform Visualizer -------------------- //
  // Runs an animation loop reading from the given Web Audio analyser node
  const startWaveformLoop = (analyser) => {
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    const draw = () => {
      animationFrameIdRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);
      const avg = dataArray.reduce((a, v) => a + v, 0) / dataArray.length;
      setIsSpeaking(avg > 20);
    };
    draw();
  };

  // -------------------- Cleanup Audio (waveform + mic stream) -------------------- //
  const cleanupAudio = useCallback(() => {
    if (animationFrameIdRef.current != null) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }
    if (audioAnalyzerRef.current) {
      const { audioContext, stream } = audioAnalyzerRef.current;
      try {
        if (stream) stream.getTracks().forEach((t) => t.stop());
      } catch (_) {}
      try {
        if (audioContext && audioContext.state !== "closed") audioContext.close();
      } catch (_) {}
      audioAnalyzerRef.current = null;
    }
    // Also stop the dedicated mic stream ref (safety net)
    if (micStreamRef.current) {
      try {
        micStreamRef.current.getTracks().forEach((t) => t.stop());
      } catch (_) {}
      micStreamRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  // -------------------- Stop Recording -------------------- //
  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      // 0. Stop WebSpeech fallback if running
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (_) {}
        recognitionRef.current = null;
      }

      // 1. Stop MediaRecorder
      const recorder = mediaRecorderRef.current;
      if (recorder) {
        mediaRecorderRef.current = null;
        try {
          if (recorder.state !== "inactive") recorder.stop();
        } catch (_) {}
      }

      // 2. Close Deepgram WebSocket
      const socket = socketRef.current;
      if (socket) {
        socketRef.current = null;
        try {
          if (socket.readyState === WebSocket.OPEN) {
            // Send empty byte buffer to signal end-of-stream, then close
            socket.send(new Uint8Array(0));
          }
          socket.close();
        } catch (_) {}
      }

      // 3. Cleanup waveform + mic
      cleanupAudio();
      setIsRecording(false);
      setLiveTranscript("");
      resolve();
    });
  }, [cleanupAudio]);

  // -------------------- Start Recording -------------------- //
  const startRecording = useCallback(async (questionIndex) => {
    if (isStartingRef.current) {
      console.warn("[Deepgram] Already starting, skipping duplicate call for Q" + questionIndex);
      return;
    }

    // If already recording for the SAME question, don't restart
    if (
      socketRef.current &&
      socketRef.current.readyState === WebSocket.OPEN &&
      activeQuestionRef.current === questionIndex
    ) {
      console.log("[Deepgram] Already recording for Q" + questionIndex + ", skipping restart");
      return;
    }

    isStartingRef.current = true;
    const mySession = ++sessionCountRef.current;

    try {
      // Stop any previous session
      if (socketRef.current || mediaRecorderRef.current) {
        await stopRecording();
      }

      // If a newer session started while we awaited, bail
      if (mySession !== sessionCountRef.current) {
        console.warn("[Deepgram] Session superseded before init");
        return;
      }

      activeQuestionRef.current = questionIndex;
      bufferRef.current[questionIndex] = "";
      // Only reset answer if this is a NEW question (not a re-entry for the same question)
      if (answersRef.current[questionIndex] === undefined) {
        answersRef.current[questionIndex] = "";
      }
      setLiveTranscript("");
      updateDisplayTranscript(questionIndex);

      // ── Get Microphone Stream (shared for MediaRecorder + Waveform) ──
      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      micStreamRef.current = micStream;

      // ── Waveform: Attach analyser to the same mic stream ──
      try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const src = audioCtx.createMediaStreamSource(micStream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        src.connect(analyser);
        audioAnalyzerRef.current = { audioContext: audioCtx, analyser, stream: micStream };
        startWaveformLoop(analyser);
      } catch (err) {
        console.warn("[Mic] Waveform visualizer unavailable (non-fatal):", err.message);
      }

      // ── Deepgram WebSocket Config ──
      const deepgramKey = (process.env.REACT_APP_DEEPGRAM_API_KEY || "").trim();

      // ── WebSpeech Fallback Helper ──
      const startWebSpeechFallback = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
          console.error("[WebSpeech] Web Speech API not supported in this browser.");
          setIsRecording(false);
          return;
        }

        try {
          if (recognitionRef.current) {
            try { recognitionRef.current.stop(); } catch (_) {}
          }

          console.log("[WebSpeech] 🎙️ Starting Web Speech API fallback for Q" + questionIndex);
          const recognition = new SpeechRecognition();
          recognition.continuous = true;
          recognition.interimResults = true;
          recognition.lang = "en-IN";

          recognition.onstart = () => {
            if (mySession !== sessionCountRef.current) return;
            console.log("[WebSpeech] ✅ Web Speech API recording started for Q" + questionIndex);
            setIsRecording(true);
          };

          recognition.onresult = (event) => {
            if (mySession !== sessionCountRef.current) return;
            let interim = "";
            let finalStr = "";

            for (let i = event.resultIndex; i < event.results.length; ++i) {
              const transcript = event.results[i][0]?.transcript || "";
              if (event.results[i].isFinal) {
                finalStr += " " + transcript;
              } else {
                interim += " " + transcript;
              }
            }

            if (finalStr.trim()) {
              const corrected = correctText(finalStr.trim());
              const prevText = answersRef.current[questionIndex] || "";
              answersRef.current[questionIndex] = dedupeText(prevText ? prevText + " " + corrected : corrected);
              bufferRef.current[questionIndex] = "";
              setLiveTranscript("");
              setIsSpeaking(false);
              updateDisplayTranscript(questionIndex);
            } else if (interim.trim()) {
              const corrected = correctText(interim.trim());
              bufferRef.current[questionIndex] = corrected;
              setLiveTranscript(corrected);
              setIsSpeaking(true);
              updateDisplayTranscript(questionIndex);
            }
          };

          recognition.onerror = (err) => {
            if (mySession !== sessionCountRef.current) return;
            console.warn("[WebSpeech] SpeechRecognition error:", err.error);
          };

          recognition.onend = () => {
            if (mySession !== sessionCountRef.current) return;
            if (isRecording && recognitionRef.current) {
              try { recognition.start(); } catch (_) {}
            }
          };

          recognition.start();
          recognitionRef.current = recognition;
        } catch (err) {
          console.error("[WebSpeech] Failed to start Web Speech API:", err);
          setIsRecording(false);
        }
      };

      if (!deepgramKey) {
        console.warn("[Deepgram] API key missing! Falling back to Web Speech API.");
        startWebSpeechFallback();
        return;
      }

      // Build keyterm params for Deepgram Nova-3 (top terms to boost recognition)
      // Nova-3 uses `keyterm` instead of `keywords`
      const topKeyterms = (techTerms || []).slice(0, 50);
      const keytermParams = topKeyterms
        .map((t) => `keyterm=${encodeURIComponent(t)}`)
        .join("&");

      const wsUrl =
        `wss://api.deepgram.com/v1/listen` +
        `?model=nova-3` +
        `&language=en-IN` +
        `&punctuate=true` +
        `&interim_results=true` +
        `&smart_format=true` +
        `&endpointing=300` +
        `&utterance_end_ms=1500` +
        `&token=${encodeURIComponent(deepgramKey)}` +
        (keytermParams ? `&${keytermParams}` : "");

      console.log("[Deepgram] Connecting for Q" + questionIndex + " | Key length:", deepgramKey.length);

      const socket = new WebSocket(wsUrl, ["token", deepgramKey]);
      socketRef.current = socket;

      // ── WebSocket: Open → start sending mic audio ──
      socket.onopen = () => {
        if (mySession !== sessionCountRef.current) return;
        console.log("[Deepgram] ✅ Connection opened for Q" + questionIndex);
        setIsRecording(true);

        // Determine best supported audio container format
        const mimeType = getSupportedMimeType();
        const recorderOptions = mimeType ? { mimeType } : undefined;

        try {
          const recorder = new MediaRecorder(micStream, recorderOptions);
          mediaRecorderRef.current = recorder;

          recorder.ondataavailable = (event) => {
            if (mySession !== sessionCountRef.current) return;
            if (
              event.data.size > 0 &&
              socketRef.current &&
              socketRef.current.readyState === WebSocket.OPEN
            ) {
              socketRef.current.send(event.data);
            }
          };

          // Send audio chunks every 250ms for low-latency transcription
          recorder.start(250);
          console.log("[Deepgram] MediaRecorder started | mimeType:", recorder.mimeType);
        } catch (err) {
          console.error("[Deepgram] MediaRecorder failed to start:", err);
        }
      };

      // ── WebSocket: Messages → process transcription results ──
      socket.onmessage = (event) => {
        if (mySession !== sessionCountRef.current) return;

        try {
          const data = JSON.parse(event.data);

          // Only process transcription results
          if (data.type !== "Results") return;

          const alt = data.channel?.alternatives?.[0];
          if (!alt) return;

          const transcript = (alt.transcript || "").trim();
          if (!transcript) return;

          const corrected = correctText(transcript);

          if (data.is_final) {
            // ── Final result — commit to answersRef (equivalent to Azure's "recognized") ──
            const prevText = answersRef.current[questionIndex] || "";
            if (corrected) {
              const combined = prevText ? prevText + " " + corrected : corrected;
              answersRef.current[questionIndex] = dedupeText(combined);
            }
            // Clear the interim buffer since the phrase has been committed
            bufferRef.current[questionIndex] = "";
            setLiveTranscript("");
            setIsSpeaking(false);
            updateDisplayTranscript(questionIndex);
          } else {
            // ── Interim result — show as live preview (equivalent to Azure's "recognizing") ──
            bufferRef.current[questionIndex] = corrected;
            setLiveTranscript(corrected);
            setIsSpeaking(true);
            updateDisplayTranscript(questionIndex);
          }
        } catch (err) {
          console.warn("[Deepgram] Message parse error:", err);
        }
      };

      // ── WebSocket: Error ──
      socket.onerror = (event) => {
        if (mySession !== sessionCountRef.current) return;
        console.error("[Deepgram] ❌ WebSocket error, switching to WebSpeech fallback:", event);
        startWebSpeechFallback();
      };

      // ── WebSocket: Close ──
      socket.onclose = (event) => {
        if (mySession !== sessionCountRef.current) return;
        console.log(
          "[Deepgram] Connection closed. Code:", event.code,
          "| Reason:", event.reason || "(none)"
        );
        // If closed abnormally (not 1000 normal closure), try fallback
        if (event.code !== 1000 && event.code !== 1005) {
          console.warn("[Deepgram] Abnormal close, switching to WebSpeech fallback");
          startWebSpeechFallback();
        } else {
          setIsRecording(false);
          setLiveTranscript("");
        }
      };
    } catch (err) {
      console.error("[Deepgram] Exception in startRecording:", err);
      setIsRecording(false);
      cleanupAudio();
    } finally {
      isStartingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopRecording, cleanupAudio, updateDisplayTranscript]);

  // -------------------- Save Answer -------------------- //
  const saveAnswer = useCallback((questionIndex) => {
    const bufferText = bufferRef.current[questionIndex]?.trim() || "";
    if (bufferText) {
      const prev = answersRef.current[questionIndex] || "";
      const normPrev = normalizeText(prev);
      const normBuffer = normalizeText(bufferText);
      
      // Calculate word overlap to prevent adding buffer if it's already represented in prev
      const wordsBuffer = normBuffer.split(/\s+/).filter(Boolean);
      const wordsPrev = normPrev.split(/\s+/).filter(Boolean);
      const matchCount = wordsBuffer.filter(w => wordsPrev.includes(w)).length;
      const isOverlap = wordsBuffer.length > 0 && (matchCount / wordsBuffer.length) > 0.5;

      if (!normPrev.includes(normBuffer) && !isOverlap) {
        answersRef.current[questionIndex] = (prev + " " + bufferText).trim();
      }
      bufferRef.current[questionIndex] = "";
    }

    const finalAnswer = dedupeText(answersRef.current[questionIndex] || "");
    answersRef.current[questionIndex] = finalAnswer;
    updateDisplayTranscript(questionIndex);
    return finalAnswer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDisplayTranscript]);

  const getAnswers = useCallback(() => answersRef.current, []);

  const restoreAnswersMap = useCallback((savedAnswersMap, currentQIndex = 0) => {
    if (savedAnswersMap && typeof savedAnswersMap === "object") {
      answersRef.current = { ...savedAnswersMap };
      activeQuestionRef.current = currentQIndex;
      updateDisplayTranscript(currentQIndex);
      console.log("[SpeechRecognizationContext] Restored answers map for refresh recovery:", answersRef.current);
    }
  }, [updateDisplayTranscript]);

  return (
    <transcriptionContext.Provider
      value={{
        startRecording,
        stopRecording,
        saveAnswer,
        getAnswers,
        restoreAnswersMap,
        activeQuestionRef,
        isRecording,
        liveTranscript,
        displayTranscript,
        isSpeaking,
      }}
    >
      {children}
    </transcriptionContext.Provider>
  );
};
